import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Copy, ExternalLink, Loader2, Monitor, QrCode, UserCheck, Vibrate, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/ui/page-header';
import { UnderlineTabs } from '../components/ui/underline-tabs';
import { StatusDot, StatusTone } from '../components/ui/status-dot';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import {
  scoringService, SCORING_HOST, SCORING_PROTOCOL, SCORING_ORIGIN,
  ScoringHeat, HeatParticipant, ValidationStatus, LiveScore, ConnectedDevice,
} from '../services/scoring.service';
import qrcode from '../lib/vendor/qrcode';

/**
 * Jour J — piloter une compétition en scoring en ligne depuis le navigateur,
 * sans installer l'Organizer (2026-11-03, maquette validée par Bryan).
 *
 * Le scoring (scoring.logfit.eu) est partagé par tous les organisateurs :
 * chaque appel porte l'événement CHOISI ici, jamais « l'événement actif » du
 * serveur. Les téléphones des juges entrent avec le QR de cet événement ; le
 * serveur ne montre et ne laisse assigner que ceux-là.
 */

const HEATS_POLL_MS = 5000;
const HEAT_POLL_MS = 2000;
const DEVICES_POLL_MS = 4000;
const COUNTDOWN_SECONDS = 10;

// Écrans servis par le scoring (crossfit-display). L'adresse porte toujours
// l'événement : une TV n'est pas connectée, et le scoring en ligne est partagé.
const SCREENS = [
  { key: 'hall', path: '/display/' },
  { key: 'overlay', path: '/overlay/' },
] as const;

function useVisiblePolling(fn: () => void, ms: number, enabled: boolean) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return;
    ref.current();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') ref.current();
    }, ms);
    return () => window.clearInterval(id);
  }, [ms, enabled]);
}

function errorText(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

// Ordre stable d'un rafraîchissement à l'autre : par nom, en ordre naturel.
const sortDevices = (list: ConnectedDevice[]) =>
  [...list].sort((a, b) => a.device_name.localeCompare(b.device_name, undefined, { numeric: true }));

const formatClock = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// ── QR des juges ─────────────────────────────────────────────────────────────

const QrSvg: React.FC<{ text: string; size?: number }> = ({ text, size = 240 }) => {
  const cells = useMemo(() => {
    qrcode.stringToBytes = qrcode.stringToBytesFuncs['UTF-8'];
    const qr = qrcode(0, 'M');
    qr.addData(text, 'Byte');
    qr.make();
    const n = qr.getModuleCount();
    const dark: string[] = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) dark.push(`M${c + 2},${r + 2}h1v1h-1z`);
    }
    return { n, d: dark.join('') };
  }, [text]);
  return (
    <svg viewBox={`0 0 ${cells.n + 4} ${cells.n + 4}`} width={size} height={size} role="img" aria-label="QR">
      <rect width="100%" height="100%" fill="#ffffff" />
      <path d={cells.d} fill="#000000" shapeRendering="crispEdges" />
    </svg>
  );
};

// Menu de couloir d'un téléphone. Composant de module (et non défini dans le
// rendu) : sinon chaque rafraîchissement le recrée et le referme sous la main.
const LaneSelect: React.FC<{
  laneCount: number;
  deviceByLane: Map<number, ConnectedDevice>;
  onPick: (lane: number) => void;
}> = ({ laneCount, deviceByLane, onPick }) => {
  const { t } = useTranslation();
  return (
    <select
      className="h-8 w-full rounded-[3px] border border-white/15 bg-transparent px-2 text-sm text-foreground"
      value=""
      aria-label={t('dayOf.chooseLane')}
      onChange={(e) => { const lane = Number(e.target.value); if (lane) onPick(lane); }}
    >
      <option value="">{t('dayOf.chooseLane')}</option>
      {Array.from({ length: laneCount }, (_, i) => i + 1).map((lane) => (
        <option key={lane} value={lane}>
          {t('dayOf.lane', { n: lane })}{deviceByLane.has(lane) ? ` · ${deviceByLane.get(lane)!.device_name}` : ''}
        </option>
      ))}
    </select>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const DayOf: React.FC = () => {
  const { t } = useTranslation();
  const { selectedEvent, refreshEvents } = useEvent();
  const navigate = useNavigate();
  const eventId = selectedEvent?.id ?? null;

  const [tab, setTab] = useState<'heats' | 'devices'>('heats');
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [scoringError, setScoringError] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);

  const [opening, setOpening] = useState(false);
  const [licenceRefus, setLicenceRefus] = useState<{ reason: string; cost: number | null; balance: number | null } | null>(null);
  const [licenceActive, setLicenceActive] = useState(false);
  const [openConfirm, setOpenConfirm] = useState<{ cost: number; balance: number } | null>(null);

  const [heats, setHeats] = useState<ScoringHeat[]>([]);
  const [selectedHeatId, setSelectedHeatId] = useState<string | null>(null);
  const [participants, setParticipants] = useState<HeatParticipant[]>([]);
  const [validation, setValidation] = useState<ValidationStatus | null>(null);
  const [scores, setScores] = useState<LiveScore[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [countdownStart, setCountdownStart] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [acting, setActing] = useState<string | null>(null);
  const [stopConfirm, setStopConfirm] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [validatingLane, setValidatingLane] = useState<number | null>(null);

  const [devices, setDevices] = useState<ConnectedDevice[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  // Changer d'événement repart de zéro : rien de l'ancien ne doit rester.
  useEffect(() => {
    setAccessCode(null); setAccessLoaded(false); setScoringError(null);
    setLicenceActive(false); setLicenceRefus(null);
    setHeats([]); setSelectedHeatId(null); setParticipants([]); setValidation(null);
    setScores([]); setStartTime(null); setDevices([]); setDevicesLoaded(false);
  }, [eventId]);

  const loadAccess = useCallback(async () => {
    if (!eventId) return;
    try {
      const access = await scoringService.getJudgeAccess(eventId);
      setAccessCode(access.access_code);
      setLicenceActive(access.license_active);
      setScoringError(null);
    } catch (e) {
      setScoringError(errorText(e, t('dayOf.errors.scoringUnreachable')));
    } finally {
      setAccessLoaded(true);
    }
  }, [eventId, t]);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  // Ouvert = activé ET licencié : sans licence, le scoring refuse de préparer
  // et de lancer (402), même si l'événement est actif.
  const isOpen = !!accessCode && selectedEvent?.status === 'active' && licenceActive;

  // Heats de l'événement
  useVisiblePolling(async () => {
    if (!eventId) return;
    try {
      const list = await scoringService.getHeats(eventId);
      list.sort((a, b) =>
        `${a.heat_date ?? ''} ${a.start_time ?? ''}`.localeCompare(`${b.heat_date ?? ''} ${b.start_time ?? ''}`)
        || a.heat_number - b.heat_number);
      setHeats(list);
      setSelectedHeatId((cur) => cur ?? (list.find((h) => h.status !== 'finished') ?? list[0])?.id ?? null);
    } catch (e) {
      setScoringError(errorText(e, t('dayOf.errors.scoringUnreachable')));
    }
  }, HEATS_POLL_MS, isOpen);

  const selectedHeat = heats.find((h) => h.id === selectedHeatId) ?? null;

  const loadHeatDetail = useCallback(async () => {
    if (!selectedHeatId) return;
    try {
      const [p, v, s, st] = await Promise.all([
        scoringService.getParticipants(selectedHeatId),
        scoringService.getValidation(selectedHeatId),
        scoringService.getLiveScores(selectedHeatId),
        scoringService.getHeatStatus(selectedHeatId),
      ]);
      setParticipants(p); setValidation(v); setScores(s);
      // État d'exécution de la heat choisie, relu ici toutes les 2 s : la liste
      // des heats (5 s) raterait une bonne partie du décompte de 10 s.
      const live = st?.live_status;
      const toMs = (raw?: string | null) => (raw ? new Date(raw).getTime() : null);
      setStartTime(toMs(live?.start_time));
      setCountdownStart(live?.status === 'countdown' ? toMs(live?.countdown_start) : null);
      if (live?.status) {
        setHeats((list) => list.map((h) => (h.id === selectedHeatId && h.status !== live.status
          ? { ...h, status: live.status as string } : h)));
      }
    } catch (e) {
      setScoringError(errorText(e, t('dayOf.errors.scoringUnreachable')));
    }
  }, [selectedHeatId, t]);

  useEffect(() => {
    setParticipants([]); setValidation(null); setScores([]); setStartTime(null); setCountdownStart(null);
  }, [selectedHeatId]);
  useVisiblePolling(loadHeatDetail, HEAT_POLL_MS, isOpen && tab === 'heats' && !!selectedHeatId);

  // Le chrono et le décompte tournent ici entre deux lectures du serveur.
  const runStatus = selectedHeat?.status;
  useEffect(() => {
    if (runStatus !== 'running' && runStatus !== 'countdown') return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [runStatus]);

  // Téléphones (onglet Appareils, et colonne de la vue Heats)
  useVisiblePolling(async () => {
    if (!eventId) return;
    try {
      setDevices(sortDevices(await scoringService.getDevices(eventId)));
    } catch (e) {
      setScoringError(errorText(e, t('dayOf.errors.scoringUnreachable')));
    } finally {
      setDevicesLoaded(true);
    }
  }, DEVICES_POLL_MS, isOpen);

  // ── Ouvrir l'événement pour le jour J ──────────────────────────────────────

  const startOpening = async () => {
    if (!eventId) return;
    setOpening(true);
    setLicenceRefus(null);
    try {
      const v = await apiService.validateEventLicense(eventId);
      if (v?.auto_consume) {
        setOpenConfirm({ cost: v.token_cost ?? 0, balance: v.token_balance ?? 0 });
        return;
      }
      await finishOpening(false);
    } catch (e: any) {
      // Refus de licence (403) : jetons insuffisants ou aucun produit ne couvre
      // l'événement. On le dit dans la page, avec de quoi acheter des jetons.
      const refus = e?.response?.status === 403 ? e?.response?.data?.detailObjet : null;
      if (refus) {
        setLicenceRefus({
          reason: refus.reason ?? t('dayOf.errors.openFailed'),
          cost: typeof refus.token_cost === 'number' ? refus.token_cost : null,
          balance: typeof refus.token_balance === 'number' ? refus.token_balance : null,
        });
      } else {
        toast.error(errorText(e, t('dayOf.errors.openFailed')));
      }
    } finally {
      setOpening(false);
    }
  };

  const finishOpening = async (consume: boolean) => {
    if (!eventId) return;
    setOpening(true);
    try {
      if (consume) await apiService.consumeTokens(eventId);
      await scoringService.activateEvent(eventId);
      await refreshEvents();
      await loadAccess();
      setOpenConfirm(null);
      toast.success(t('dayOf.opened'));
    } catch (e) {
      toast.error(errorText(e, t('dayOf.errors.openFailed')));
    } finally {
      setOpening(false);
    }
  };

  // ── Heats ──────────────────────────────────────────────────────────────────

  const heatAction = async (action: 'prepare' | 'start' | 'stop' | 'reset') => {
    if (!selectedHeatId) return;
    setActing(action);
    try {
      await scoringService.heatAction(selectedHeatId, action);
      setHeats((list) => list.map((h) => h.id === selectedHeatId
        ? { ...h, status: action === 'prepare' ? 'preparing' : action === 'start' ? 'countdown' : action === 'stop' ? 'finished' : 'ready' }
        : h));
      await loadHeatDetail();
    } catch (e) {
      toast.error(errorText(e, t(`dayOf.errors.${action}`)));
    } finally {
      setActing(null);
      setStopConfirm(false);
      setResetConfirm(false);
    }
  };

  const validateLane = async (lane: number) => {
    if (!selectedHeatId) return;
    setValidatingLane(lane);
    try {
      setValidation(await scoringService.validateLane(selectedHeatId, lane));
    } catch (e) {
      toast.error(errorText(e, t('dayOf.errors.validateLane')));
    } finally {
      setValidatingLane(null);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('dayOf.screens.copied'));
    } catch {
      toast.error(t('dayOf.screens.copyFailed'));
    }
  };

  const heatGroups = useMemo(() => {
    const groups: { key: string; label: string; heats: ScoringHeat[] }[] = [];
    for (const h of heats) {
      const key = h.wod_id ?? 'none';
      let g = groups.find((x) => x.key === key);
      if (!g) {
        g = { key, label: h.wod_name || t('dayOf.noWod'), heats: [] };
        groups.push(g);
      }
      g.heats.push(h);
    }
    return groups;
  }, [heats, t]);

  const statusLabel = (status: string): { tone: StatusTone; label: string } => {
    switch (status) {
      case 'preparing': return { tone: 'warning', label: t('dayOf.status.preparing') };
      case 'countdown': return { tone: 'warning', label: t('dayOf.status.countdown') };
      case 'running': return { tone: 'success', label: t('dayOf.status.running') };
      case 'rest': return { tone: 'success', label: t('dayOf.status.rest') };
      case 'finished': return { tone: 'neutral', label: t('dayOf.status.finished') };
      default: return { tone: 'neutral', label: t('dayOf.status.scheduled') };
    }
  };

  const deviceByLane = useMemo(() => {
    const m = new Map<number, ConnectedDevice>();
    devices.forEach((d) => { if (d.lane_number != null) m.set(d.lane_number, d); });
    return m;
  }, [devices]);

  const laneRows = useMemo(() => {
    return participants.map((p) => {
      const v = validation?.lanes.find((l) => l.lane_number === p.lane_number);
      const s = scores.find((x) => x.lane_number === p.lane_number);
      return { ...p, ready: !!v?.is_validated, device: deviceByLane.get(p.lane_number), score: s };
    });
  }, [participants, validation, scores, deviceByLane]);

  const scoreText = (s?: LiveScore) => {
    if (!s) return '—';
    if (s.announced_weight != null) return `${s.announced_weight} kg${s.is_successful === false ? ' ✗' : ''}`;
    if (s.is_finished && s.finish_time != null) return formatClock(s.finish_time);
    return `${s.reps_completed ?? 0}`;
  };

  // ── Couloirs (onglet Appareils) ────────────────────────────────────────────

  const laneCount = useMemo(() => {
    let n = 0;
    heats.forEach((h) => { n = Math.max(n, h.max_participants ?? 0); });
    devices.forEach((d) => { n = Math.max(n, d.lane_number ?? 0); });
    return n || 10;
  }, [heats, devices]);

  const unassigned = devices.filter((d) => d.lane_number == null);

  const assign = async (deviceId: string, lane: number) => {
    if (!eventId) return;
    try {
      await scoringService.assignDevice(deviceId, lane, eventId);
      setDevices(sortDevices(await scoringService.getDevices(eventId)));
    } catch (e) {
      toast.error(errorText(e, t('dayOf.errors.assign')));
    }
  };

  const unassign = async (deviceId: string) => {
    if (!eventId) return;
    try {
      await scoringService.unassignDevice(deviceId);
      setDevices(sortDevices(await scoringService.getDevices(eventId)));
    } catch (e) {
      toast.error(errorText(e, t('dayOf.errors.unassign')));
    }
  };

  const identify = async (deviceId: string) => {
    try {
      await scoringService.identifyDevice(deviceId);
    } catch (e) {
      toast.error(errorText(e, t('dayOf.errors.identify')));
    }
  };

  const batteryText = (d: ConnectedDevice) => (d.battery_level == null ? '' : `${d.battery_level} %`);

  // ── Rendu ─────────────────────────────────────────────────────────────────

  if (!selectedEvent) {
    return <p className="p-6 text-sm text-muted-foreground">{t('dayOf.noEvent')}</p>;
  }

  const qrPayload = accessCode ? JSON.stringify({
    logfit: true,
    host: SCORING_HOST,
    protocol: SCORING_PROTOCOL,
    event_id: selectedEvent.id,
    event_name: selectedEvent.name,
    access_code: accessCode,
  }) : null;

  const elapsed = startTime && selectedHeat?.status !== 'countdown' ? (now - startTime) / 1000 : 0;
  // 10 s : la durée du décompte côté scoring (websocket_manager, countdown_seconds).
  const countdownLeft = selectedHeat?.status === 'countdown' && countdownStart
    ? Math.max(0, Math.ceil(COUNTDOWN_SECONDS - (now - countdownStart) / 1000)) : null;
  const heatStatus = selectedHeat ? statusLabel(selectedHeat.status) : null;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title={t('dayOf.title')}
        meta={`${selectedEvent.name} · ${t('dayOf.onlineScoring')}`}
        actions={isOpen ? (
          <>
            <StatusDot tone="success" label={t('dayOf.openBadge')} className="text-sm text-muted-foreground" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <Monitor size={16} /> {t('dayOf.screens.title')} <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {SCREENS.map((screen, i) => {
                  const url = `${SCORING_ORIGIN}${screen.path}?event=${encodeURIComponent(selectedEvent.id)}`;
                  return (
                    <React.Fragment key={screen.key}>
                      {i > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="font-normal">
                        <div className="text-sm text-foreground">{t(`dayOf.screens.${screen.key}`)}</div>
                        <div className="text-xs text-muted-foreground">{t(`dayOf.screens.${screen.key}Hint`)}</div>
                      </DropdownMenuLabel>
                      <DropdownMenuItem onSelect={() => window.open(url, '_blank', 'noopener')}>
                        <ExternalLink size={14} /> {t('dayOf.screens.open')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => copyLink(url)}>
                        <Copy size={14} /> {t('dayOf.screens.copy')}
                      </DropdownMenuItem>
                    </React.Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="secondary" onClick={() => setQrOpen(true)}>
              <QrCode size={16} /> {t('dayOf.judgeQr')}
            </Button>
          </>
        ) : undefined}
      />

      {scoringError && (
        <p className="text-sm text-destructive">{scoringError}</p>
      )}

      {!accessLoaded ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : !isOpen ? (
        <div className="max-w-xl space-y-3 border-t border-white/10 pt-6">
          <h2 className="font-anton text-xl uppercase">{t('dayOf.notOpenTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('dayOf.notOpenBody')}</p>
          <Button onClick={startOpening} disabled={opening}>
            {opening && <Loader2 size={16} className="animate-spin" />} {t('dayOf.openAction')}
          </Button>
          {licenceRefus && (
            <div className="space-y-2 border-l-2 border-l-destructive pl-4 pt-1">
              <p className="text-sm text-foreground">{licenceRefus.reason}</p>
              {licenceRefus.cost != null && (
                <Button variant="secondary" onClick={() => navigate('/admin/my-license?action=buy')}>
                  {t('dayOf.licence.buy')}
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <UnderlineTabs
            items={[
              { value: 'heats', label: t('dayOf.tabs.heats') },
              { value: 'devices', label: t('dayOf.tabs.devices', { count: devices.length }) },
            ]}
            value={tab}
            onChange={(v) => setTab(v as 'heats' | 'devices')}
          />

          {tab === 'heats' ? (
            <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)_220px]">
              {/* Heats */}
              <div className="space-y-4">
                {heats.length === 0 && <p className="text-sm text-muted-foreground">{t('dayOf.noHeats')}</p>}
                {heatGroups.map((g) => (
                  <div key={g.key}>
                    <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</div>
                    {g.heats.map((h) => {
                      const st = statusLabel(h.status);
                      const active = h.id === selectedHeatId;
                      return (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => setSelectedHeatId(h.id)}
                          className={`block w-full border-b border-white/10 px-3 py-2 text-left text-sm transition-colors hover:bg-white/5 ${active ? 'border-l-2 border-l-white bg-white/[0.07]' : ''}`}
                        >
                          <div className={active ? 'font-medium' : ''}>
                            {h.start_time ? h.start_time.slice(0, 5) : '--:--'} · {t('dayOf.heatN', { n: h.heat_number })}
                          </div>
                          <div className="text-xs text-muted-foreground">{st.label}</div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Heat choisie */}
              <div className="min-w-0">
                {selectedHeat ? (
                  <>
                    <div className="mb-3 flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">
                          {t('dayOf.heatN', { n: selectedHeat.heat_number })}
                          {(selectedHeat.category_name || selectedHeat.category_group_name) && ` · ${selectedHeat.category_name || selectedHeat.category_group_name}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {heatStatus && <StatusDot tone={heatStatus.tone} label={heatStatus.label} />}
                          {validation && ` · ${t('dayOf.judgesReady', { ready: validation.validated_count, total: validation.required_count })}`}
                        </div>
                      </div>
                      {countdownLeft != null ? (
                        <div className="font-['DS-Digital'] text-4xl tabular-nums tracking-wide text-warning" aria-live="polite">
                          {countdownLeft}
                        </div>
                      ) : (
                        <div className="font-['DS-Digital'] text-3xl tabular-nums tracking-wide">{formatClock(elapsed)}</div>
                      )}
                    </div>

                    <div className="overflow-hidden rounded-[5px] border border-white/14">
                      <table className="w-full table-fixed text-sm">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                            <th className="w-12 px-3 py-2 font-normal"></th>
                            <th className="px-3 py-2 font-normal">{t('dayOf.cols.team')}</th>
                            <th className="w-48 px-3 py-2 font-normal">{t('dayOf.cols.phone')}</th>
                            <th className="w-24 px-3 py-2 text-right font-normal">{t('dayOf.cols.score')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {laneRows.length === 0 && (
                            <tr><td colSpan={4} className="px-3 py-4 text-muted-foreground">{t('dayOf.noTeams')}</td></tr>
                          )}
                          {laneRows.map((r) => (
                            <tr key={r.lane_number} className="border-b border-white/[0.08] last:border-0">
                              <td className="px-3 py-2">
                                <span className="inline-block w-7 rounded-[2px] bg-white text-center font-medium text-black">{r.lane_number}</span>
                              </td>
                              <td className="truncate px-3 py-2">{r.team_name}</td>
                              <td className="px-3 py-2">
                                {/* « Prêt » ne veut dire quelque chose que pendant la
                                    préparation ; ensuite on montre le téléphone. */}
                                {selectedHeat.status === 'preparing' ? (
                                  r.ready ? (
                                    <StatusDot tone="success" label={t('dayOf.ready')} />
                                  ) : (
                                    <span className="flex items-center gap-2">
                                      {r.device
                                        ? <StatusDot tone="warning" label={t('dayOf.notReady')} />
                                        : <StatusDot tone="destructive" label={t('dayOf.noPhone')} />}
                                      {/* Juge sans téléphone : l'organisateur valide à sa place,
                                          comme « Valider manuellement » dans l'Organizer. */}
                                      <button type="button" disabled={validatingLane === r.lane_number}
                                        className="text-muted-foreground hover:text-success disabled:opacity-50"
                                        aria-label={t('dayOf.validateLane')} title={t('dayOf.validateLane')}
                                        onClick={() => validateLane(r.lane_number)}>
                                        {validatingLane === r.lane_number
                                          ? <Loader2 size={15} className="animate-spin" />
                                          : <UserCheck size={15} />}
                                      </button>
                                    </span>
                                  )
                                ) : !r.device
                                  ? <StatusDot tone="destructive" label={t('dayOf.noPhone')} />
                                  : <span className="truncate text-muted-foreground">{r.device.device_name}</span>}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{scoreText(r.score)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                      {(selectedHeat.status === 'ready' || selectedHeat.status === 'finished') && (
                        <Button variant="secondary" disabled={!!acting}
                          onClick={() => (selectedHeat.status === 'finished' ? setResetConfirm(true) : heatAction('prepare'))}>
                          {acting && <Loader2 size={16} className="animate-spin" />}
                          {selectedHeat.status === 'finished' ? t('dayOf.actions.reset') : t('dayOf.actions.prepare')}
                        </Button>
                      )}
                      {selectedHeat.status === 'preparing' && (
                        <>
                          <Button variant="outline" disabled={!!acting} onClick={() => heatAction('reset')}>
                            {t('dayOf.actions.cancelPrepare')}
                          </Button>
                          <Button disabled={!!acting || !validation?.all_ready} onClick={() => heatAction('start')}>
                            {acting === 'start' && <Loader2 size={16} className="animate-spin" />} {t('dayOf.actions.start')}
                          </Button>
                        </>
                      )}
                      {['countdown', 'running', 'rest'].includes(selectedHeat.status) && (
                        <Button variant="destructive" disabled={!!acting} onClick={() => setStopConfirm(true)}>
                          {t('dayOf.actions.stop')}
                        </Button>
                      )}
                    </div>
                    {selectedHeat.status === 'preparing' && !validation?.all_ready && (
                      <p className="mt-1 text-right text-xs text-muted-foreground">{t('dayOf.waitJudges')}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('dayOf.pickHeat')}</p>
                )}
              </div>

              {/* Téléphones */}
              <div>
                <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t('dayOf.phonesConnected', { count: devices.length })}
                </div>
                {devices.length === 0 && devicesLoaded && (
                  <p className="py-2 text-sm text-muted-foreground">{t('dayOf.noPhones')}</p>
                )}
                {devices.map((d) => (
                  <div key={d.device_id} className="border-b border-white/10 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{d.device_name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {d.lane_number != null ? t('dayOf.lane', { n: d.lane_number }) : batteryText(d)}
                      </span>
                    </div>
                    {d.lane_number == null && <div className="mt-1"><LaneSelect laneCount={laneCount} deviceByLane={deviceByLane} onPick={(lane) => assign(d.device_id, lane)} /></div>}
                  </div>
                ))}
                <p className="mt-2 text-xs text-muted-foreground">{t('dayOf.phonesHint')}</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
              {/* Couloirs */}
              <div className="min-w-0">
                <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">{t('dayOf.lanesTitle')}</div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: laneCount }, (_, i) => i + 1).map((lane) => {
                    const d = deviceByLane.get(lane);
                    return (
                      <div key={lane}
                        className={`flex items-center gap-3 rounded-[5px] border px-3 py-2 text-sm ${d ? 'border-white/14' : 'border-dashed border-white/15'}`}>
                        <span className="inline-block w-7 shrink-0 rounded-[2px] bg-white text-center font-medium text-black">{lane}</span>
                        {d ? (
                          <>
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{d.device_name}</div>
                              <div className="text-xs text-muted-foreground">{batteryText(d) || ' '}</div>
                            </div>
                            <button type="button" className="text-muted-foreground hover:text-foreground"
                              aria-label={t('dayOf.identify')} title={t('dayOf.identify')} onClick={() => identify(d.device_id)}>
                              <Vibrate size={16} />
                            </button>
                            <button type="button" className="text-muted-foreground hover:text-destructive"
                              aria-label={t('dayOf.unassign')} title={t('dayOf.unassign')} onClick={() => unassign(d.device_id)}>
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <span className="text-muted-foreground">{t('dayOf.emptyLane')}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* À placer */}
              <div>
                <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t('dayOf.toPlace', { count: unassigned.length })}
                </div>
                {unassigned.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {devices.length === 0 ? t('dayOf.noPhones') : t('dayOf.allPlaced')}
                  </p>
                )}
                {unassigned.map((d) => (
                  <div key={d.device_id} className="space-y-1 border-b border-white/10 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{d.device_name}</span>
                      <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                        {batteryText(d)}
                        <button type="button" className="hover:text-foreground" aria-label={t('dayOf.identify')}
                          title={t('dayOf.identify')} onClick={() => identify(d.device_id)}>
                          <Vibrate size={16} />
                        </button>
                      </span>
                    </div>
                    <LaneSelect laneCount={laneCount} deviceByLane={deviceByLane} onPick={(lane) => assign(d.device_id, lane)} />
                  </div>
                ))}
                <p className="mt-3 text-xs text-muted-foreground">{t('dayOf.phonesHint')}</p>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{t('dayOf.judgeQr')}</DialogTitle></DialogHeader>
          {qrPayload && (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-[5px] bg-white p-3"><QrSvg text={qrPayload} /></div>
              <div className="text-center text-sm text-muted-foreground">
                {t('dayOf.qrHint')}
                <div className="mt-2 font-mono text-2xl tracking-[0.2em] text-foreground">{accessCode}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!openConfirm}
        onOpenChange={(o) => { if (!o) setOpenConfirm(null); }}
        title={t('dayOf.openConfirmTitle')}
        message={t('dayOf.openConfirmBody', { cost: openConfirm?.cost ?? 0, balance: openConfirm?.balance ?? 0 })}
        confirmLabel={t('dayOf.openAction')}
        loading={opening}
        onConfirm={() => finishOpening(true)}
      />

      <ConfirmDialog
        open={stopConfirm}
        onOpenChange={setStopConfirm}
        title={t('dayOf.stopConfirmTitle')}
        message={t('dayOf.stopConfirmBody')}
        confirmLabel={t('dayOf.actions.stop')}
        destructive
        loading={acting === 'stop'}
        onConfirm={() => heatAction('stop')}
      />

      <ConfirmDialog
        open={resetConfirm}
        onOpenChange={setResetConfirm}
        title={t('dayOf.resetConfirmTitle')}
        message={t('dayOf.resetConfirmBody')}
        confirmLabel={t('dayOf.actions.reset')}
        destructive
        loading={acting === 'reset'}
        onConfirm={() => heatAction('reset')}
      />
    </div>
  );
};

export default DayOf;
